import type { ItemBeneficiaryShare } from './specific-gift-defaults';

/** The shape the redemption beneficiary pickers render. */
export interface NamedContact {
  id: number;
  firstName: string;
  lastName: string;
}

/**
 * **The matter's contacts as a whole-asset beneficiary picker lists them**: a
 * person by name, an organisation by its name in the first-name slot, anyone
 * with neither dropped. Shared by the Redemption tab's "other" asset sheet and
 * the record's instruction panel (VALE-1940), so both list the same people.
 */
export function namedBeneficiaryContacts(
  contacts: ReadonlyArray<{
    id: number;
    firstName?: string | null;
    lastName?: string | null;
    organisationName?: string | null;
  }>
): NamedContact[] {
  return contacts.flatMap((c) => {
    if (c.firstName || c.lastName)
      return [
        { id: c.id, firstName: c.firstName ?? '', lastName: c.lastName ?? '' }
      ];
    if (c.organisationName)
      return [{ id: c.id, firstName: c.organisationName, lastName: '' }];
    return [];
  });
}

export interface SeededBeneficiary {
  contactId: number;
  name: string;
}

/**
 * **The default beneficiary selection for an instruction that allocates
 * nothing** — a vehicle or an "other" asset (VALE-1431).
 *
 * A share transfer splits units and a property transfer splits percentages, so
 * each needs its own allocator; a vehicle or a miscellaneous asset transfers
 * whole, so the entitlement determines only WHO. The shares are therefore read
 * for their contacts and their percentages are deliberately unused.
 *
 * Contacts the picker cannot show are dropped, for the same reason
 * `buildEntitlementSeed` drops non-beneficiaries: seeding a row the lawyer
 * cannot see or deselect would inflate the saved selection past what is on
 * screen. Order follows the entitlement, so two beneficiaries seed in the order
 * the will names them.
 *
 * Returns `null` rather than `[]` for "seed nothing", so a caller can tell a
 * gift with no showable beneficiary apart from a deliberate empty selection.
 */
export function buildContactBeneficiarySeed(
  shares: ItemBeneficiaryShare[] | undefined,
  contacts: NamedContact[]
): SeededBeneficiary[] | null {
  if (!shares?.length) return null;
  const seed = shares
    .map((share) => contacts.find((c) => c.id === share.contactId))
    .filter((c): c is NamedContact => !!c)
    .map((c) => ({
      contactId: c.id,
      name: `${c.firstName} ${c.lastName}`.trim()
    }));
  return seed.length > 0 ? seed : null;
}
